<template>
  <v-card
    v-if="newGameImageInfo && newGameOnTagClick"
    class="new-game-dialog-card"
    height="90vh"
  >
    <v-card-title>New Game</v-card-title>

    <v-container :fluid="true">
      <div class="new-game-dialog-layout">
        <div
          class="area-image has-image"
          style="min-height: 50vh;"
        >
          <PuzzleCropper
            :image="newGameImageInfo"
            :puzzle-creation-info="puzzleCreationInfo"
            :shape-mode="shapeMode"
            :pieces-preview="tab === 'settings'"
            @crop-update="onCropUpdate"
          />
        </div>

        <div
          class="area-settings"
          :class="isImageApproved ? '' : 'has-unapproved-image'"
        >
          <v-alert
            v-if="!isImageApproved && !forcePrivate"
            type="info"
          >
            This image you chose is pending approval. Public games created with this image will not show up
            on the public games list until the image is approved. You can still share the game link
            with others and puzzle it together.
          </v-alert>
          <v-tabs v-model="tab">
            <v-tab value="settings">
              Settings
            </v-tab>
            <v-tab value="image-info">
              Image Info
            </v-tab>
          </v-tabs>

          <v-window v-model="tab">
            <v-window-item
              value="settings"
              class="window-settings"
            >
              <v-form
                ref="form"
                v-model="valid"
              >
                <fieldset>
                  <legend>Pieces Amount</legend>
                  <v-text-field
                    v-model="pieces"
                    type="number"
                    step="100"
                    min="0"
                    max="5000"
                    density="compact"
                    :rules="[numberRule]"
                    class="pieces-input"
                  />
                  <span>
                    Resulting pieces: {{ valid ? puzzleCreationInfo.pieceCount : '-' }}
                  </span>
                </fieldset>
                <div>
                  <v-label><v-icon icon="mdi-cog-outline mr-1" /> Misc</v-label>
                  <v-checkbox
                    v-model="showImagePreviewInBackground"
                    density="comfortable"
                    label="Show image preview in background"
                    hide-details
                  />
                </div>
                <div>
                  <v-label><v-icon icon="mdi-counter mr-1" /> Scoring</v-label>
                  <v-radio-group
                    v-model="scoreMode"
                    density="comfortable"
                  >
                    <v-radio
                      :label="`${scoreModeToString(ScoreMode.ANY)} (${scoreModeDescriptionToString(ScoreMode.ANY)})`"
                      :value="ScoreMode.ANY"
                    />
                    <v-radio
                      :label="`${scoreModeToString(ScoreMode.FINAL)} (${scoreModeDescriptionToString(ScoreMode.FINAL)})`"
                      :value="ScoreMode.FINAL"
                    />
                  </v-radio-group>
                </div>
                <div>
                  <v-label><v-icon icon="mdi-shape mr-1" /> Shapes</v-label>
                  <v-radio-group
                    v-model="shapeMode"
                    density="comfortable"
                  >
                    <v-radio
                      :label="`${shapeModeToString(ShapeMode.NORMAL)} (${shapeModeDescriptionToString(ShapeMode.NORMAL)})`"
                      :value="ShapeMode.NORMAL"
                    />
                    <v-radio
                      :label="`${shapeModeToString(ShapeMode.ANY)} (${shapeModeDescriptionToString(ShapeMode.ANY)})`"
                      :value="ShapeMode.ANY"
                    />
                    <v-radio
                      :label="`${shapeModeToString(ShapeMode.FLAT)} (${shapeModeDescriptionToString(ShapeMode.FLAT)})`"
                      :value="ShapeMode.FLAT"
                    />
                  </v-radio-group>
                </div>
                <div>
                  <v-label><v-icon icon="mdi-connection mr-1" /> Snapping</v-label>
                  <v-radio-group
                    v-model="snapMode"
                    density="comfortable"
                  >
                    <v-radio
                      :label="`${snapModeToString(SnapMode.NORMAL)} (${snapModeDescriptionToString(SnapMode.NORMAL)})`"
                      :value="SnapMode.NORMAL"
                    />
                    <v-radio
                      :label="`${snapModeToString(SnapMode.REAL)} (${snapModeDescriptionToString(SnapMode.REAL)})`"
                      :value="SnapMode.REAL"
                    />
                  </v-radio-group>
                </div>
                <div>
                  <v-label><v-icon icon="mdi-format-rotate-90 mr-1" /> Rotation</v-label>
                  <v-radio-group
                    v-model="rotationMode"
                    density="comfortable"
                  >
                    <v-radio
                      :label="`${rotationModeToString(RotationMode.NONE)} (${rotationModeDescriptionToString(RotationMode.NONE)})`"
                      :value="RotationMode.NONE"
                    />
                    <v-radio
                      :label="`${rotationModeToString(RotationMode.ORTHOGONAL)} (${rotationModeDescriptionToString(RotationMode.ORTHOGONAL)})`"
                      :value="RotationMode.ORTHOGONAL"
                    />
                  </v-radio-group>
                </div>
                <div>
                  <v-label><v-icon icon="mdi-incognito mr-1" /> Privacy</v-label>
                  <v-checkbox
                    v-model="isPrivate"
                    density="comfortable"
                    label="Private Game"
                    :disabled="forcePrivate"
                    hide-details
                  />
                  <v-checkbox
                    v-model="requireAccount"
                    density="comfortable"
                    label="Require account (Anonymous users won't be able to join)"
                    :disabled="!isPrivate"
                    hide-details
                  />
                  <v-checkbox
                    v-model="requirePassword"
                    density="comfortable"
                    label="Require password to join"
                    :disabled="!isPrivate"
                    hide-details
                  />
                  <v-text-field
                    v-model="joinPassword"
                    label="Join Password"
                    type="password"
                    density="compact"
                    :disabled="!isPrivate || !requirePassword"
                  />
                </div>
              </v-form>
            </v-window-item>
            <v-window-item value="image-info">
              <ImageInfoTable
                :image="newGameImageInfo"
                @tag-click="newGameOnTagClick"
              />
            </v-window-item>
          </v-window>

          <v-card-actions>
            <v-btn
              variant="elevated"
              :disabled="!canStartNewGame"
              prepend-icon="mdi-puzzle"
              color="success"
              @click="onNewGameClick"
            >
              Generate Puzzle
            </v-btn>
            <v-btn
              variant="elevated"
              color="error"
              @click="closeDialog()"
            >
              Cancel
            </v-btn>
          </v-card-actions>
        </div>
      </div>
    </v-container>
  </v-card>
</template>
<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

import { RotationMode, ScoreMode, ShapeMode, SnapMode, IMAGE_STATES_VISIBLE } from '@common/Types'
import type { GameSettings } from '@common/Types'
import { NEWGAME_MIN_PIECES, NEWGAME_MAX_PIECES } from '@common/GameCommon'
import PuzzleCropper from '../PuzzleCropper.vue'
import { determinePuzzleInfo } from '@common/Puzzle'
import type { PuzzleCreationInfo } from '@common/Puzzle'
import type { Rect } from '@common/Geometry'
import ImageInfoTable from '../ImageInfoTable.vue'
import {
  rotationModeDescriptionToString,
  rotationModeToString,
  scoreModeDescriptionToString,
  scoreModeToString,
  shapeModeDescriptionToString,
  shapeModeToString,
  snapModeDescriptionToString,
  snapModeToString,
} from '@common/Util'
import { useDialog } from '../../useDialog'

const { newGameImageInfo, newGameOnNewGameClick, newGameOnTagClick, closeDialog } = useDialog()

const tab = ref<string>('settings')

const forcePrivate = ref<boolean>(newGameImageInfo.value?.private || false)
const pieces = ref<string | number>(1000)
const isPrivate = ref<boolean>(forcePrivate.value)
const showImagePreviewInBackground = ref<boolean>(false)
const requireAccount = ref<boolean>(false)
const requirePassword = ref<boolean>(false)
const joinPassword = ref<string>('')
const scoreMode = ref<ScoreMode>(ScoreMode.ANY)
const shapeMode = ref<ShapeMode>(ShapeMode.NORMAL)
const snapMode = ref<SnapMode>(SnapMode.NORMAL)
const rotationMode = ref<RotationMode>(RotationMode.NONE)

const crop = ref<Rect>({ x: 0, y: 0, w: newGameImageInfo.value?.width || 0, h: newGameImageInfo.value?.height  || 0 })

const valid = ref<boolean>(true)

// prevent clicking the button multiple times
const creating = ref<boolean>(false)

const canStartNewGame = computed((): boolean => {
  if (!newGameImageInfo.value) {
    return false
  }
  if (creating.value) {
    return false
  }
  if (!valid.value) {
    return false
  }
  if (
    !piecesInt.value
    || !newGameImageInfo.value
    || !newGameImageInfo.value.url
    || ![0, 1].includes(scoreModeInt.value)
  ) {
    return false
  }
  return true
})

const isImageApproved = computed((): boolean => {
  const state = newGameImageInfo.value?.state
  return state ? IMAGE_STATES_VISIBLE.includes(state) : false
})

const scoreModeInt = computed((): number => {
  return parseInt(`${scoreMode.value}`, 10)
})
const shapeModeInt = computed((): number => {
  return parseInt(`${shapeMode.value}`, 10)
})
const snapModeInt = computed((): number => {
  return parseInt(`${snapMode.value}`, 10)
})
const rotationModeInt = computed((): number => {
  return parseInt(`${rotationMode.value}`, 10)
})
const piecesInt = computed((): number => {
  return parseInt(`${pieces.value}`, 10)
})
const puzzleCreationInfo = computed((): PuzzleCreationInfo => {
  const dim = { w: newGameImageInfo.value?.width || 0, h: newGameImageInfo.value?.height || 0 }
  return determinePuzzleInfo(dim, piecesInt.value)
})

const numberRule = (v: string) => {
  const num = parseInt(v, 10)
  if (!isNaN(num) && num >= NEWGAME_MIN_PIECES && num <= NEWGAME_MAX_PIECES) {
    return true
  }
  return 'Pieces have to be between 10 and 5000'
}

const onCropUpdate = (newCrop: Rect) => {
  crop.value = newCrop
}

const onNewGameClick = async () => {
  if (!newGameImageInfo.value || !newGameOnNewGameClick.value) {
    return
  }

  creating.value = true

  const isPriv = isPrivate.value
  const reqAccount = isPriv ? requireAccount.value : false
  const joinPass = isPriv && requirePassword.value ? joinPassword.value || null : null
  const gameSettings: GameSettings = {
    tiles: piecesInt.value,
    private: isPriv,
    requireAccount: reqAccount,
    joinPassword: joinPass,
    image: newGameImageInfo.value,
    scoreMode: scoreModeInt.value,
    shapeMode: shapeModeInt.value,
    snapMode: snapModeInt.value,
    rotationMode: rotationModeInt.value,
    crop: crop.value,
    showImagePreviewInBackground: showImagePreviewInBackground.value,
  }
  await newGameOnNewGameClick.value(gameSettings)
}

const form = ref<any>()

onMounted(() => {
  // @ts-ignore
  form.value.validate()
})
</script>
